import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  MdAccountBalanceWallet,
  MdArrowOutward,
  MdGroups,
  MdRateReview,
  MdWarningAmber
} from 'react-icons/md'
import { FaMoneyBillTransfer, FaMoneyCheckDollar } from 'react-icons/fa6'
import { HiOutlineCreditCard } from 'react-icons/hi2'

export default function Activity() {
  const { url } = useSelector(state => state)

  const [stats, setStats] = useState({
    total_customers: 0,
    active_loans: 0,
    manual_reviews: 0,
    awaiting_disbursement: 0,
    overdue_loans: 0,
    total_disbursed: 0,
    total_repayments: 0,
    total_outstanding: 0
  })

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const formatCurrency = (amount) =>
    `₦${Number(amount || 0).toLocaleString()}`

  useEffect(() => {
    const getDashboardStats = async () => {
      try {
        setError('')

        const response = await axios.get(
          `${url}/admin/dashboard-stats`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('access_token')}`
            }
          }
        )

        setStats(response.data)
      } catch (err) {
        console.error(err)
        setError('Unable to load dashboard statistics.')
      } finally {
        setLoading(false)
      }
    }

    getDashboardStats()
  }, [url])

  const analytics = [
    {
      title: 'Total customers',
      value: stats.total_customers,
      helper: 'Registered RentHub users',
      icon: MdGroups,
      tone: 'bg-blue-50 text-blue-600'
    },
    {
      title: 'Active loans',
      value: stats.active_loans,
      helper: 'Currently in repayment',
      icon: HiOutlineCreditCard,
      tone: 'bg-green-50 text-green-600'
    },
    {
      title: 'Manual reviews',
      value: stats.manual_reviews,
      helper: 'Need an admin decision',
      icon: MdRateReview,
      tone: 'bg-amber-50 text-amber-600'
    },
    {
      title: 'Awaiting disbursement',
      value: stats.awaiting_disbursement,
      helper: 'Approved landlord payouts',
      icon: FaMoneyBillTransfer,
      tone: 'bg-violet-50 text-violet-600'
    },
    {
      title: 'Overdue loans',
      value: stats.overdue_loans,
      helper: 'Require repayment attention',
      icon: MdWarningAmber,
      tone: 'bg-red-50 text-red-600'
    },
    {
      title: 'Total disbursed',
      value: formatCurrency(stats.total_disbursed),
      helper: 'Rent paid to landlords',
      icon: FaMoneyCheckDollar,
      tone: 'bg-emerald-50 text-emerald-600'
    },
    {
      title: 'Repayments received',
      value: formatCurrency(stats.total_repayments),
      helper: 'Customer repayments recorded',
      icon: MdArrowOutward,
      tone: 'bg-cyan-50 text-cyan-600'
    },
    {
      title: 'Outstanding balance',
      value: formatCurrency(stats.total_outstanding),
      helper: 'Remaining portfolio balance',
      icon: MdAccountBalanceWallet,
      tone: 'bg-slate-100 text-slate-700'
    }
  ]

  return (
    <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1500px]">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-green-600">OPERATIONS OVERVIEW</p>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Welcome back, Admin</h1>
            <p className="mt-2 text-sm leading-6 text-gray-500">
              A live view of RentHub applications, disbursements and repayments.
            </p>
          </div>

          <div className="w-fit rounded-full border border-green-100 bg-green-50 px-3.5 py-2 text-xs font-semibold text-green-700">
            System overview
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
            {error}
          </div>
        )}

        {loading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="h-36 animate-pulse rounded-2xl border border-gray-200 bg-white" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {analytics.map(item => {
              const Icon = item.icon

              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}>
                      <Icon className="text-xl" />
                    </div>
                  </div>

                  <p className="mt-5 text-sm font-medium text-gray-500">{item.title}</p>
                  <p className="mt-1 break-words text-2xl font-bold tracking-tight text-gray-900">{item.value}</p>
                  <p className="mt-2 text-xs leading-5 text-gray-400">{item.helper}</p>
                </div>
              )
            })}
          </div>
        )}

        <div className="mt-8 rounded-2xl border border-gray-200 bg-[#101828] p-6 text-white shadow-sm lg:p-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold text-green-400">RENTAL CREDIT OPERATIONS</p>
            <h2 className="mt-2 text-2xl font-bold">Keep the lifecycle clean from approval to final repayment.</h2>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              Review borderline applications, disburse approved rent to landlords, and use the overdue queue to keep repayment issues visible.
            </p>
          </div>
        </div>
      </div>
    </main>
  )
}
